// Lógica pura da tela Cessão (S-2231).
// buildRequestBody: estado da tela -> corpo camelCase (CessaoFilter) dos endpoints do BFF.
// buildChartVM: DTO de dashboard do BFF -> view-model do gráfico de remuneração por período.

export interface CessaoFiltros {
  originCnpjList: string[];
  destinationCnpjList: string[];
  tpRegPrevList: string[];
  startDate?: string;
  endDate?: string;
  // Declaração de remuneração no órgão de origem: true=com (ônus cedente), false=sem, null=ambos.
  originRemunDeclarationToggle: boolean | null;
  // true=só cedidos, false=só não cedidos, null=ambos.
  isCeded: boolean | null;
}

export interface CessaoState {
  busca: string;
  filtros: CessaoFiltros;
  isCededOnCurrentDate: boolean;
}

// Corpo (CessaoFilter) enviado aos endpoints dashboard/count/history/export.
export interface RequestBody {
  rootCnpjList: string[];
  filterTerm: string | null;
  originCnpjList: string[];
  destinationCnpjList: string[];
  tpRegPrevList: string[];
  startDate: string | null;
  endDate: string | null;
  originRemunDeclarationToggle: boolean | null;
  isCeded: boolean | null;
  isCededOnCurrentDate: boolean;
}

export function buildRequestBody(s: CessaoState): RequestBody {
  const term = s.busca.trim();
  // Toggle "Cedidos na data corrente" limpa os demais filtros (paridade com o Vue).
  if (s.isCededOnCurrentDate) {
    return {
      rootCnpjList: [],
      filterTerm: term || null,
      originCnpjList: [],
      destinationCnpjList: [],
      tpRegPrevList: [],
      startDate: null,
      endDate: null,
      originRemunDeclarationToggle: null,
      isCeded: null,
      isCededOnCurrentDate: true,
    };
  }
  return {
    rootCnpjList: [],
    filterTerm: term || null,
    originCnpjList: s.filtros.originCnpjList,
    destinationCnpjList: s.filtros.destinationCnpjList,
    tpRegPrevList: s.filtros.tpRegPrevList,
    startDate: s.filtros.startDate ?? null,
    endDate: s.filtros.endDate ?? null,
    originRemunDeclarationToggle: s.filtros.originRemunDeclarationToggle,
    isCeded: s.filtros.isCeded,
    isCededOnCurrentDate: false,
  };
}

// situação (código do BFF) -> rótulo PT-BR exibido na tabela.
export const SITUACAO_LABEL: Record<string, string> = {
  ceded: 'Cedido',
  active: 'Não Cedido',
};

// ---- Dashboard DTO (espelha o response do BFF) ----

export interface CessionRemunerationByPeriod {
  keys: string[]; // "yyyy-MM"
  series: { name: string; data: number[] }[];
}

export interface DashboardDTO {
  cessionRemunerationByPeriod?: CessionRemunerationByPeriod[];
  cessionProportionByRemuneration?: {
    remuneracaoCedenteCount: number;
    remuneracaoCessionarioCount: number;
  };
}

export interface ChartSeries {
  name: string;
  data: number[];
}

export interface ChartVM {
  // rótulos do eixo x já formatados ("JAN 2025", ...).
  categories: string[];
  series: ChartSeries[];
  isEmpty: boolean;
}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

// "yyyy-MM" -> "MMM YYYY" em maiúsculas (ex.: "2025-01" -> "JAN 2025").
export function formatPeriodKey(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key ?? '');
  if (!m) return (key ?? '').toUpperCase();
  const mes = MESES[Number(m[2]) - 1] ?? m[2];
  return `${mes} ${m[1]}`;
}

export function buildChartVM(d?: DashboardDTO): ChartVM {
  const block = d?.cessionRemunerationByPeriod?.[0];
  const keys = block?.keys ?? [];
  const series = (block?.series ?? []).map((s) => ({ name: s.name, data: s.data ?? [] }));
  return {
    categories: keys.map(formatPeriodKey),
    series,
    isEmpty: keys.length === 0,
  };
}
